import { useEffect, useMemo, useState, useCallback } from 'react';
import { Loader2, Check, X, AlertTriangle, Search, Download, Pencil, Plus, Trash2, Sparkles } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@easyflow/ui-frontend/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@easyflow/ui-frontend/components/ui/select';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@easyflow/ui-frontend/components/ui/dialog';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { usePermissions } from '@/contexts/PermissionContext';
import apiClient from '../../lib/api';
import { getErrorMessage } from '../../lib/utils';


type SkillLevel = 'base' | 'intermedio' | 'avanzato' | 'esperto';

const LEVEL_RANK: Record<SkillLevel, number> = {
  base: 1,
  intermedio: 2,
  avanzato: 3,
  esperto: 4,
};

const LEVEL_LABEL: Record<SkillLevel, string> = {
  base: 'Base',
  intermedio: 'Intermedio',
  avanzato: 'Avanzato',
  esperto: 'Esperto',
};

const LEVELS: SkillLevel[] = ['base', 'intermedio', 'avanzato', 'esperto'];

interface SkillRow { id: string; name: string; color: string | null }
interface UserRow { id: string; first_name: string; last_name: string }
interface UserSkillRow { user_id: string; skill_id: string; skill_level: SkillLevel | null }
interface ServiceSkillRow {
  product_id: string;
  product_name: string;
  service_type_id: string | null;
  skill_id: string | null;
  min_skill_level: SkillLevel | null;
}

interface ServiceAggregate {
  product_id: string;
  product_name: string;
  required: { skill_id: string; min_level: SkillLevel }[];
}

interface MatrixData {
  skills: SkillRow[];
  users: UserRow[];
  user_skills: UserSkillRow[];
  service_skills: ServiceSkillRow[];
}

function normalizeLevel(v: string | null | undefined): SkillLevel {
  if (v === 'intermedio' || v === 'avanzato' || v === 'esperto' || v === 'base') return v;
  return 'base';
}

export default function SkillMatrixSettings() {
  const { hasPermission } = usePermissions();
  const canEditSkills = hasPermission('hr.settings.manage');
  const canEditServices = hasPermission('pos.catalogo');
  const { toast } = useToast();
  const [data, setData] = useState<MatrixData | null>(null);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [onlyGaps, setOnlyGaps] = useState(false);
  const [view, setView] = useState<'services' | 'skills'>('services');
  const [serviceFilter, setServiceFilter] = useState<string | null>(null);
  const [skillFilter, setSkillFilter] = useState<{ skill_id: string; bucket: 'expert' | 'base' | 'none' } | null>(null);
  const [newServiceOpen, setNewServiceOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiClient.get<MatrixData>('/calendar/skill-matrix');
      if (r.success && r.data) setData(r.data);
    } catch (e) {
      toast({ title: 'Errore', description: getErrorMessage(e) || 'Impossibile caricare la matrice', variant: 'destructive' });
    } finally { setLoading(false); }
  }, [toast]);
  useEffect(() => { load(); }, [load]);

  const services: ServiceAggregate[] = useMemo(() => {
    if (!data) return [];
    const map = new Map<string, ServiceAggregate>();
    for (const r of data.service_skills) {
      let s = map.get(r.product_id);
      if (!s) {
        s = { product_id: r.product_id, product_name: r.product_name, required: [] };
        map.set(r.product_id, s);
      }
      if (r.skill_id) {
        s.required.push({ skill_id: r.skill_id, min_level: normalizeLevel(r.min_skill_level) });
      }
    }
    return Array.from(map.values()).sort((a, b) => a.product_name.localeCompare(b.product_name));
  }, [data]);

  const skillsById = useMemo(() => {
    const m = new Map<string, SkillRow>();
    if (data) for (const s of data.skills) m.set(s.id, s);
    return m;
  }, [data]);

  // user_id -> skill_id -> level
  const userSkillMap = useMemo(() => {
    const m = new Map<string, Map<string, SkillLevel>>();
    if (data) {
      for (const us of data.user_skills) {
        let inner = m.get(us.user_id);
        if (!inner) { inner = new Map(); m.set(us.user_id, inner); }
        inner.set(us.skill_id, normalizeLevel(us.skill_level));
      }
    }
    return m;
  }, [data]);

  const filteredUsers = useMemo(() => {
    if (!data) return [];
    const q = filter.trim().toLowerCase();
    let list = data.users;
    if (q) {
      list = list.filter(u => `${u.first_name} ${u.last_name}`.toLowerCase().includes(q));
    }
    return list;
  }, [data, filter]);

  const serviceStats = useMemo(() => {
    const m = new Map<string, { ok: number; partial: number; missing: number; total: number }>();
    if (!data) return m;
    for (const svc of services) {
      let ok = 0, partial = 0, missing = 0;
      for (const u of filteredUsers) {
        if (svc.required.length === 0) continue;
        const userSkills = userSkillMap.get(u.id) || new Map<string, SkillLevel>();
        let miss = 0, under = 0;
        for (const req of svc.required) {
          const lvl = userSkills.get(req.skill_id);
          if (!lvl) miss++;
          else if (LEVEL_RANK[lvl] < LEVEL_RANK[req.min_level]) under++;
        }
        if (miss === 0 && under === 0) ok++;
        else if (miss === svc.required.length) missing++;
        else partial++;
      }
      m.set(svc.product_id, { ok, partial, missing, total: ok + partial + missing });
    }
    return m;
  }, [data, services, userSkillMap, filteredUsers]);

  useEffect(() => {
    if (serviceFilter && !services.some(s => s.product_id === serviceFilter)) {
      setServiceFilter(null);
    }
  }, [services, serviceFilter]);

  const skillStats = useMemo(() => {
    const m = new Map<string, { expert: number; base: number; none: number; total: number }>();
    if (!data) return m;
    for (const s of data.skills) {
      let expert = 0, base = 0, none = 0;
      for (const u of filteredUsers) {
        const lvl = (userSkillMap.get(u.id) || new Map<string, SkillLevel>()).get(s.id);
        if (!lvl) none++;
        else if (LEVEL_RANK[lvl] >= LEVEL_RANK.avanzato) expert++;
        else base++;
      }
      m.set(s.id, { expert, base, none, total: expert + base + none });
    }
    return m;
  }, [data, filteredUsers, userSkillMap]);

  useEffect(() => {
    if (skillFilter && data && !data.skills.some(s => s.id === skillFilter.skill_id)) {
      setSkillFilter(null);
    }
  }, [data, skillFilter]);

  function evaluateService(userId: string, svc: ServiceAggregate) {
    if (svc.required.length === 0) return { status: 'no-req' as const };
    const userSkills = userSkillMap.get(userId) || new Map();
    let missing = 0;
    let underRank = 0;
    for (const req of svc.required) {
      const lvl = userSkills.get(req.skill_id);
      if (!lvl) missing++;
      else if (LEVEL_RANK[lvl as keyof typeof LEVEL_RANK] < LEVEL_RANK[req.min_level as keyof typeof LEVEL_RANK]) underRank++;
    }
    if (missing === 0 && underRank === 0) return { status: 'ok' as const };
    if (missing === svc.required.length) return { status: 'missing' as const, missing, underRank };
    return { status: 'partial' as const, missing, underRank };
  }

  async function persistUserSkills(userId: string, nextLevels: Map<string, SkillLevel>) {
    if (!canEditSkills) return;
    if (!data) return;
    const payload = Array.from(nextLevels.entries()).map(([skill_id, level]) => ({
      skill_id, skill_level: level,
    }));
    setSavingKey(userId);
    try {
      const r = await apiClient.put(`/hr/users/${userId}/skills`, { skills: payload });
      if (!r.success) throw new Error(r.error || 'Salvataggio fallito');
      // Refresh local state by mutating user_skills for this user
      setData(prev => {
        if (!prev) return prev;
        const others = prev.user_skills.filter(us => us.user_id !== userId);
        const added = payload.map(p => ({
          user_id: userId, skill_id: p.skill_id, skill_level: p.skill_level,
        }));
        return { ...prev, user_skills: [...others, ...added] };
      });
      toast({ title: 'Aggiornato', description: 'Competenze salvate' });
    } catch (e) {
      toast({ title: 'Errore', description: getErrorMessage(e) || 'Salvataggio fallito', variant: 'destructive' });
    } finally {
      setSavingKey(null);
    }
  }

  function exportCsv() {
    if (!data) return;
    const csvEscape = (v: string) => {
      const needs = /[",;\n\r]/.test(v);
      const s = v.replace(/"/g, '""');
      return needs ? `"${s}"` : s;
    };
    const sep = ';';
    const lines: string[] = [];
    const today = new Date().toISOString().slice(0, 10);
    let filename = `matrice-competenze-${today}.csv`;

    if (view === 'services') {
      const header = ['Risorsa', ...services.map(s => s.product_name), 'Stato complessivo'];
      lines.push(header.map(csvEscape).join(sep));
      for (const u of filteredUsers) {
        const evals = services.map(svc => ({ svc, ev: evaluateService(u.id, svc) }));
        if (onlyGaps && !evals.some(e => e.ev.status === 'partial' || e.ev.status === 'missing')) continue;
        const row: string[] = [`${u.first_name} ${u.last_name}`];
        let gaps = 0; let okCount = 0; let withReq = 0;
        for (const { svc, ev } of evals) {
          if (ev.status === 'no-req') { row.push('—'); continue; }
          withReq++;
          const userSkills = userSkillMap.get(u.id) || new Map<string, SkillLevel>();
          const detail = svc.required.map(req => {
            const lvl = userSkills.get(req.skill_id);
            const skill = skillsById.get(req.skill_id);
            return `${skill?.name || '?'}:${lvl ? LEVEL_LABEL[lvl] : '∅'}/≥${LEVEL_LABEL[req.min_level]}`;
          }).join(' | ');
          const label = ev.status === 'ok' ? 'Idonea' : ev.status === 'partial' ? 'Parziale' : 'Manca';
          if (ev.status === 'ok') okCount++; else gaps++;
          row.push(`${label} (${detail})`);
        }
        row.push(withReq === 0 ? 'n/d' : `${okCount}/${withReq} idonee${gaps ? `, ${gaps} gap` : ''}`);
        lines.push(row.map(csvEscape).join(sep));
      }
      filename = `matrice-servizi-${today}.csv`;
    } else {
      const header = ['Risorsa', ...data.skills.map(s => s.name)];
      lines.push(header.map(csvEscape).join(sep));
      for (const u of filteredUsers) {
        const row: string[] = [`${u.first_name} ${u.last_name}`];
        const userSkills = userSkillMap.get(u.id) || new Map<string, SkillLevel>();
        for (const s of data.skills) {
          const lvl = userSkills.get(s.id);
          row.push(lvl ? `${LEVEL_LABEL[lvl]} (${LEVEL_RANK[lvl]})` : '');
        }
        lines.push(row.map(csvEscape).join(sep));
      }
      filename = `matrice-competenze-${today}.csv`;
    }

    const csv = '﻿' + lines.join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast({ title: 'Esportato', description: filename });
  }

  async function setSingleUserSkill(userId: string, skillId: string, level: SkillLevel | null) {
    if (!canEditSkills) return;
    const current = new Map(userSkillMap.get(userId) || new Map<string, SkillLevel>());
    if (level === null) current.delete(skillId);
    else current.set(skillId, level);
    await persistUserSkills(userId, current);
  }

  async function persistServiceRequirements(productId: string, requirements: { skill_id: string; min_level: SkillLevel }[]): Promise<boolean> {
    if (!canEditServices) return false;
    if (!data) return false;
    const payload = requirements.map(r => ({ skill_id: r.skill_id, min_skill_level: r.min_level }));
    setSavingKey(`svc:${productId}`);
    try {
      const r = await apiClient.put(`/pos/products/${productId}/skills`, { requirements: payload });
      if (!r.success) throw new Error(r.error || 'Salvataggio fallito');
      const product = services.find(s => s.product_id === productId);
      const productName = product?.product_name || '';
      setData(prev => {
        if (!prev) return prev;
        const others = prev.service_skills.filter(ss => ss.product_id !== productId);
        const apptTypeId = prev.service_skills.find(ss => ss.product_id === productId)?.service_type_id ?? null;
        const added: ServiceSkillRow[] = requirements.length > 0
          ? requirements.map(req => ({
              product_id: productId,
              product_name: productName,
              service_type_id: apptTypeId,
              skill_id: req.skill_id,
              min_skill_level: req.min_level,
            }))
          : [{
              product_id: productId,
              product_name: productName,
              service_type_id: apptTypeId,
              skill_id: null,
              min_skill_level: null,
            }];
        return { ...prev, service_skills: [...others, ...added] };
      });
      toast({ title: 'Aggiornato', description: 'Requisiti del servizio salvati' });
      return true;
    } catch (e) {
      toast({ title: 'Errore', description: getErrorMessage(e) || 'Salvataggio fallito', variant: 'destructive' });
      return false;
    } finally {
      setSavingKey(null);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="space-y-4 rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h3 className="text-base font-semibold">Matrice Competenze</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Visualizza chi può svolgere quale servizio in base alle competenze HR. Modifica i livelli con un click.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={filter}
              onChange={e => setFilter(e.target.value)}
              placeholder="Cerca risorsa…"
              className="w-48 rounded-md border border-gray-300 bg-white py-1.5 pl-7 pr-2 text-sm dark:border-gray-600 dark:bg-gray-800"
            />
          </div>
          <label className="flex items-center gap-1 text-xs text-gray-600 dark:text-gray-300">
            <input type="checkbox" checked={onlyGaps} onChange={e => setOnlyGaps(e.target.checked)} />
            Solo gap
          </label>
          <div className="flex rounded-md border border-gray-300 dark:border-gray-600">
            <button
              onClick={() => setView('services')}
              className={`px-2 py-1 text-xs ${view === 'services' ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/30' : ''}`}
            >Servizi</button>
            <button
              onClick={() => setView('skills')}
              className={`px-2 py-1 text-xs ${view === 'skills' ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/30' : ''}`}
            >Competenze</button>
          </div>
          <Button variant="outline" size="sm" onClick={exportCsv} title="Esporta la matrice corrente in CSV">
            <Download className="mr-1 h-3.5 w-3.5" /> Esporta CSV
          </Button>
          <Button variant="default" size="sm" disabled={!canEditServices} onClick={() => { if (canEditServices) setNewServiceOpen(true); }} title="Crea o collega un servizio a un tipo appuntamento">
            <Sparkles className="mr-1 h-3.5 w-3.5" /> Nuovo servizio
          </Button>
          <Button variant="outline" size="sm" onClick={load}>Aggiorna</Button>
        </div>
      </div>

      <NewServiceModal
        open={newServiceOpen}
        onOpenChange={setNewServiceOpen}
        existingProductIds={new Set(services.map(s => s.product_id))}
        allSkills={data.skills}
        onCreated={() => { setNewServiceOpen(false); load(); }}
      />

      {view === 'services' ? (
        <>
          {serviceFilter && (() => {
            const svc = services.find(s => s.product_id === serviceFilter);
            if (!svc) return null;
            return (
              <div className="flex items-center justify-between rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
                <span>Filtro attivo: solo risorse parziali o mancanti per <strong>{svc.product_name}</strong></span>
                <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => setServiceFilter(null)}>Rimuovi filtro</Button>
              </div>
            );
          })()}
          <ServiceMatrix
            users={filteredUsers}
            services={services}
            allSkills={data.skills}
            skillsById={skillsById}
            userSkillMap={userSkillMap}
            evaluateService={evaluateService}
            serviceStats={serviceStats}
            savingKey={savingKey}
            onlyGaps={onlyGaps}
            serviceFilter={serviceFilter}
            onToggleServiceFilter={(id) => setServiceFilter(prev => prev === id ? null : id)}
            onSetSkill={setSingleUserSkill}
            onSaveRequirements={persistServiceRequirements}
          />
        </>
      ) : (
        <>
          {skillFilter && (() => {
            const sk = data.skills.find(s => s.id === skillFilter.skill_id);
            if (!sk) return null;
            const label = skillFilter.bucket === 'expert'
              ? 'solo risorse esperte (avanzato/esperto)'
              : skillFilter.bucket === 'base'
                ? 'solo risorse con livello base/intermedio'
                : 'solo risorse senza questa competenza';
            return (
              <div className="flex items-center justify-between rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
                <span>Filtro attivo: {label} per <strong>{sk.name}</strong></span>
                <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => setSkillFilter(null)}>Rimuovi filtro</Button>
              </div>
            );
          })()}
          <SkillMatrix
            users={filteredUsers}
            skills={data.skills}
            userSkillMap={userSkillMap}
            savingKey={savingKey}
            skillStats={skillStats}
            skillFilter={skillFilter}
            onToggleSkillFilter={(skillId, bucket) => setSkillFilter(prev =>
              prev && prev.skill_id === skillId && prev.bucket === bucket ? null : { skill_id: skillId, bucket }
            )}
            onSetSkill={setSingleUserSkill}
          />
        </>
      )}

      <Legend />
    </div>
  );
}

function Legend() {
  return (
    <div className="flex flex-wrap items-center gap-4 border-t border-gray-200 pt-3 text-xs text-gray-600 dark:border-gray-700 dark:text-gray-400">
      <span className="flex items-center gap-1"><Check className="h-3 w-3 text-green-600" /> Idonea</span>
      <span className="flex items-center gap-1"><AlertTriangle className="h-3 w-3 text-amber-600" /> Parziale (livello basso o skill mancante)</span>
      <span className="flex items-center gap-1"><X className="h-3 w-3 text-red-500" /> Non idonea</span>
      <span className="text-gray-400">— Servizio senza requisiti di skill</span>
    </div>
  );
}

interface ServiceMatrixProps {
  users: UserRow[];
  services: ServiceAggregate[];
  allSkills: SkillRow[];
  skillsById: Map<string, SkillRow>;
  userSkillMap: Map<string, Map<string, SkillLevel>>;
  evaluateService: (userId: string, svc: ServiceAggregate) => { status: 'ok' | 'partial' | 'missing' | 'no-req'; missing?: number; underRank?: number };
  serviceStats: Map<string, { ok: number; partial: number; missing: number; total: number }>;
  savingKey: string | null;
  onlyGaps: boolean;
  serviceFilter: string | null;
  onToggleServiceFilter: (productId: string) => void;
  onSetSkill: (userId: string, skillId: string, level: SkillLevel | null) => void | Promise<void>;
  onSaveRequirements: (productId: string, requirements: { skill_id: string; min_level: SkillLevel }[]) => Promise<boolean>;
}

function ServiceMatrix({ users, services, allSkills, skillsById, userSkillMap, evaluateService, serviceStats, savingKey, onlyGaps, serviceFilter, onToggleServiceFilter, onSetSkill, onSaveRequirements }: ServiceMatrixProps) {
  if (services.length === 0) {
    return <div className="rounded-md border border-dashed border-gray-300 p-8 text-center text-sm text-gray-500">Nessun servizio configurato (servizi = prodotti POS con tipo appuntamento).</div>;
  }
  if (users.length === 0) {
    return <div className="rounded-md border border-dashed border-gray-300 p-8 text-center text-sm text-gray-500">Nessuna risorsa.</div>;
  }
  return (
    <div className="overflow-auto rounded-md border border-gray-200 dark:border-gray-700">
      <table className="min-w-full border-collapse text-sm">
        <thead className="sticky top-0 z-10 bg-gray-50 dark:bg-gray-800">
          <tr>
            <th className="sticky left-0 z-20 min-w-[180px] border-b border-r border-gray-200 bg-gray-50 px-3 py-2 text-left font-medium dark:border-gray-700 dark:bg-gray-800">Risorsa</th>
            {services.map(svc => (
              <th key={svc.product_id} className={`min-w-[160px] border-b border-r border-gray-200 px-2 py-2 text-left align-bottom font-medium dark:border-gray-700 ${serviceFilter === svc.product_id ? 'bg-amber-50 dark:bg-amber-900/20' : ''}`}>
                <ServiceHeader
                  svc={svc}
                  allSkills={allSkills}
                  skillsById={skillsById}
                  saving={savingKey === `svc:${svc.product_id}`}
                  stats={serviceStats.get(svc.product_id)}
                  filterActive={serviceFilter === svc.product_id}
                  onToggleFilter={() => onToggleServiceFilter(svc.product_id)}
                  onSave={(reqs) => onSaveRequirements(svc.product_id, reqs)}
                />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {users.map(u => {
            const evals = services.map(svc => ({ svc, eval: evaluateService(u.id, svc) }));
            if (onlyGaps && !evals.some(e => e.eval.status === 'partial' || e.eval.status === 'missing')) return null;
            if (serviceFilter) {
              const target = evals.find(e => e.svc.product_id === serviceFilter);
              if (!target || (target.eval.status !== 'partial' && target.eval.status !== 'missing')) return null;
            }
            return (
              <tr key={u.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                <td className="sticky left-0 z-10 border-b border-r border-gray-200 bg-white px-3 py-2 font-medium dark:border-gray-700 dark:bg-gray-900">
                  <div className="flex items-center gap-2">
                    {u.first_name} {u.last_name}
                    {savingKey === u.id && <Loader2 className="h-3 w-3 animate-spin text-gray-400" />}
                  </div>
                </td>
                {evals.map(({ svc, eval: ev }) => (
                  <td key={svc.product_id} className="border-b border-r border-gray-200 px-1 py-1 dark:border-gray-700">
                    <ServiceCell
                      user={u}
                      svc={svc}
                      ev={ev}
                      skillsById={skillsById}
                      userSkillMap={userSkillMap}
                      onSetSkill={onSetSkill}
                    />
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

interface ServiceCellProps {
  user: UserRow;
  svc: ServiceAggregate;
  ev: { status: 'ok' | 'partial' | 'missing' | 'no-req'; missing?: number; underRank?: number };
  skillsById: Map<string, SkillRow>;
  userSkillMap: Map<string, Map<string, SkillLevel>>;
  onSetSkill: (userId: string, skillId: string, level: SkillLevel | null) => void | Promise<void>;
}

function ServiceCell({ user, svc, ev, skillsById, userSkillMap, onSetSkill }: ServiceCellProps) {
  const { hasPermission } = usePermissions();
  const canEditSkills = hasPermission('hr.settings.manage');
  if (ev.status === 'no-req') {
    return <div className="text-center text-xs text-gray-400">—</div>;
  }
  const userSkills = userSkillMap.get(user.id) || new Map<string, SkillLevel>();
  const bg = ev.status === 'ok'
    ? 'bg-green-50 hover:bg-green-100 dark:bg-green-900/20 dark:hover:bg-green-900/40'
    : ev.status === 'partial'
      ? 'bg-amber-50 hover:bg-amber-100 dark:bg-amber-900/20 dark:hover:bg-amber-900/40'
      : 'bg-red-50 hover:bg-red-100 dark:bg-red-900/20 dark:hover:bg-red-900/40';
  const Icon = ev.status === 'ok' ? Check : ev.status === 'partial' ? AlertTriangle : X;
  const color = ev.status === 'ok' ? 'text-green-700 dark:text-green-300' : ev.status === 'partial' ? 'text-amber-700 dark:text-amber-300' : 'text-red-600 dark:text-red-400';
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className={`flex w-full items-center justify-between gap-1 rounded px-2 py-1 text-left text-xs ${bg}`}>
          <span className={`flex items-center gap-1 ${color}`}>
            <Icon className="h-3 w-3" />
            {ev.status === 'ok' ? 'Idonea' : ev.status === 'partial' ? `Parziale` : 'Manca'}
          </span>
          <span className="text-[10px] text-gray-500">{svc.required.length} skill</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72">
        <div className="mb-2 text-sm font-semibold">
          {user.first_name} {user.last_name}
        </div>
        <div className="mb-2 text-xs text-gray-600 dark:text-gray-400">{svc.product_name}</div>
        <div className="space-y-2">
          {svc.required.map(req => {
            const skill = skillsById.get(req.skill_id);
            const userLvl = userSkills.get(req.skill_id) || null;
            const meets = userLvl && LEVEL_RANK[userLvl] >= LEVEL_RANK[req.min_level];
            return (
              <div key={req.skill_id} className="flex items-center gap-2 text-xs">
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium" style={{ color: skill?.color || undefined }}>
                    {skill?.name || '—'}
                  </div>
                  <div className="text-[11px] text-gray-500">
                    Min richiesto: <strong>{LEVEL_LABEL[req.min_level]}</strong>
                    {userLvl ? (
                      <> · Attuale: <strong className={meets ? 'text-green-600' : 'text-red-600'}>{LEVEL_LABEL[userLvl]}</strong></>
                    ) : (
                      <> · <span className="text-red-600">Non assegnata</span></>
                    )}
                  </div>
                </div>
                <Select
                  disabled={!canEditSkills}
                  value={userLvl || 'none'}
                  onValueChange={(v) => onSetSkill(user.id, req.skill_id, v === 'none' ? null : v as SkillLevel)}
                >
                  <SelectTrigger className="h-7 w-28 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Rimuovi</SelectItem>
                    {LEVELS.map(l => (
                      <SelectItem key={l} value={l}>{LEVEL_LABEL[l]} ({LEVEL_RANK[l]})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

interface SkillMatrixProps {
  users: UserRow[];
  skills: SkillRow[];
  userSkillMap: Map<string, Map<string, SkillLevel>>;
  savingKey: string | null;
  skillStats: Map<string, { expert: number; base: number; none: number; total: number }>;
  skillFilter: { skill_id: string; bucket: 'expert' | 'base' | 'none' } | null;
  onToggleSkillFilter: (skillId: string, bucket: 'expert' | 'base' | 'none') => void;
  onSetSkill: (userId: string, skillId: string, level: SkillLevel | null) => void | Promise<void>;
}

function SkillMatrix({ users, skills, userSkillMap, savingKey, skillStats, skillFilter, onToggleSkillFilter, onSetSkill }: SkillMatrixProps) {
  const { hasPermission } = usePermissions();
  const canEditSkills = hasPermission('hr.settings.manage');
  if (skills.length === 0) {
    return <div className="rounded-md border border-dashed border-gray-300 p-8 text-center text-sm text-gray-500">Nessuna competenza definita.</div>;
  }
  const matchesFilter = (userId: string): boolean => {
    if (!skillFilter) return true;
    const lvl = (userSkillMap.get(userId) || new Map<string, SkillLevel>()).get(skillFilter.skill_id);
    if (skillFilter.bucket === 'none') return !lvl;
    if (!lvl) return false;
    if (skillFilter.bucket === 'expert') return LEVEL_RANK[lvl] >= LEVEL_RANK.avanzato;
    return LEVEL_RANK[lvl] < LEVEL_RANK.avanzato;
  };
  return (
    <div className="overflow-auto rounded-md border border-gray-200 dark:border-gray-700">
      <table className="min-w-full border-collapse text-sm">
        <thead className="sticky top-0 z-10 bg-gray-50 dark:bg-gray-800">
          <tr>
            <th className="sticky left-0 z-20 min-w-[180px] border-b border-r border-gray-200 bg-gray-50 px-3 py-2 text-left font-medium dark:border-gray-700 dark:bg-gray-800">Risorsa</th>
            {skills.map(s => {
              const stats = skillStats.get(s.id);
              const isFiltered = skillFilter?.skill_id === s.id;
              return (
                <th key={s.id} className={`min-w-[140px] border-b border-r border-gray-200 px-2 py-2 text-left align-top font-medium dark:border-gray-700 ${isFiltered ? 'bg-amber-50 dark:bg-amber-900/20' : ''}`}>
                  <div className="truncate" style={{ color: s.color || undefined }}>{s.name}</div>
                  {stats && stats.total > 0 && (
                    <div className="mt-1 flex flex-wrap items-center gap-0.5 text-[10px] font-normal">
                      <button
                        type="button"
                        onClick={() => onToggleSkillFilter(s.id, 'expert')}
                        title="Mostra solo risorse esperte (avanzato/esperto)"
                        className={`rounded px-1 py-0.5 ${skillFilter?.skill_id === s.id && skillFilter.bucket === 'expert' ? 'bg-green-200 text-green-900 dark:bg-green-700/40 dark:text-green-100' : 'text-green-700 hover:bg-green-100 dark:text-green-300 dark:hover:bg-green-900/30'}`}
                      >{stats.expert} esperti</button>
                      <span className="text-gray-300">·</span>
                      <button
                        type="button"
                        onClick={() => onToggleSkillFilter(s.id, 'base')}
                        title="Mostra solo risorse con livello base/intermedio"
                        className={`rounded px-1 py-0.5 ${skillFilter?.skill_id === s.id && skillFilter.bucket === 'base' ? 'bg-blue-200 text-blue-900 dark:bg-blue-700/40 dark:text-blue-100' : 'text-blue-700 hover:bg-blue-100 dark:text-blue-300 dark:hover:bg-blue-900/30'}`}
                      >{stats.base} base</button>
                      <span className="text-gray-300">·</span>
                      <button
                        type="button"
                        onClick={() => onToggleSkillFilter(s.id, 'none')}
                        title="Mostra solo risorse senza questa competenza"
                        className={`rounded px-1 py-0.5 ${skillFilter?.skill_id === s.id && skillFilter.bucket === 'none' ? 'bg-red-200 text-red-900 dark:bg-red-700/40 dark:text-red-100' : 'text-red-600 hover:bg-red-100 dark:text-red-400 dark:hover:bg-red-900/30'}`}
                      >{stats.none} senza</button>
                    </div>
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {users.map(u => {
            if (!matchesFilter(u.id)) return null;
            return (
            <tr key={u.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
              <td className="sticky left-0 z-10 border-b border-r border-gray-200 bg-white px-3 py-2 font-medium dark:border-gray-700 dark:bg-gray-900">
                <div className="flex items-center gap-2">
                  {u.first_name} {u.last_name}
                  {savingKey === u.id && <Loader2 className="h-3 w-3 animate-spin text-gray-400" />}
                </div>
              </td>
              {skills.map(s => {
                const lvl = (userSkillMap.get(u.id) || new Map<string, SkillLevel>()).get(s.id) || null;
                return (
                  <td key={s.id} className="border-b border-r border-gray-200 px-1 py-1 dark:border-gray-700">
                    <Select
                      disabled={!canEditSkills}
                      value={lvl || 'none'}
                      onValueChange={(v) => onSetSkill(u.id, s.id, v === 'none' ? null : v as SkillLevel)}
                    >
                      <SelectTrigger className="h-7 w-full text-xs">
                        <SelectValue placeholder="—" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">—</SelectItem>
                        {LEVELS.map(l => (
                          <SelectItem key={l} value={l}>{LEVEL_LABEL[l]} ({LEVEL_RANK[l]})</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                );
              })}
            </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

interface ServiceHeaderProps {
  svc: ServiceAggregate;
  allSkills: SkillRow[];
  skillsById: Map<string, SkillRow>;
  saving: boolean;
  stats?: { ok: number; partial: number; missing: number; total: number };
  filterActive: boolean;
  onToggleFilter: () => void;
  onSave: (reqs: { skill_id: string; min_level: SkillLevel }[]) => Promise<boolean>;
}

function ServiceHeader({ svc, allSkills, skillsById, saving, stats, filterActive, onToggleFilter, onSave }: ServiceHeaderProps) {
  const { hasPermission } = usePermissions();
  const canEditServices = hasPermission('pos.catalogo');
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<{ skill_id: string; min_level: SkillLevel }[]>(svc.required);

  useEffect(() => {
    if (open) setDraft(svc.required.map(r => ({ ...r })));
  }, [open, svc.required]);

  const usedIds = new Set(draft.map(d => d.skill_id));
  const available = allSkills.filter(s => !usedIds.has(s.id));

  const addSkill = (skillId: string) => {
    if (!skillId || usedIds.has(skillId)) return;
    setDraft(prev => [...prev, { skill_id: skillId, min_level: 'base' }]);
  };
  const removeSkill = (skillId: string) => {
    setDraft(prev => prev.filter(r => r.skill_id !== skillId));
  };
  const setLevel = (skillId: string, level: SkillLevel) => {
    setDraft(prev => prev.map(r => r.skill_id === skillId ? { ...r, min_level: level } : r));
  };

  const handleSave = async () => {
    if (!canEditServices) return;
    const ok = await onSave(draft);
    if (ok) setOpen(false);
  };

  return (
    <Popover open={canEditServices && open} onOpenChange={setOpen}>
      <div className="space-y-1">
        <PopoverTrigger asChild>
          <button
            disabled={!canEditServices}
            className="group w-full text-left"
            title="Modifica requisiti del servizio"
          >
            <div className="flex items-center gap-1 text-xs">
              <span className="truncate">{svc.product_name}</span>
              {saving
                ? <Loader2 className="h-3 w-3 shrink-0 animate-spin text-gray-400" />
                : <Pencil className="h-3 w-3 shrink-0 text-gray-400 opacity-0 group-hover:opacity-100" />
              }
            </div>
            <div className="mt-1 flex flex-wrap gap-1">
              {svc.required.length === 0 && <span className="text-[10px] text-gray-400">nessun requisito</span>}
              {svc.required.map(req => {
                const skill = skillsById.get(req.skill_id);
                return (
                  <span
                    key={req.skill_id}
                    title={`${skill?.name || ''} ≥ ${LEVEL_LABEL[req.min_level]}`}
                    className="inline-flex items-center gap-1 rounded px-1 py-0.5 text-[10px]"
                    style={{ backgroundColor: (skill?.color || '#e5e7eb') + '33', color: skill?.color || '#374151' }}
                  >
                    {skill?.name || '?'} <span className="opacity-70">≥{LEVEL_RANK[req.min_level]}</span>
                  </span>
                );
              })}
            </div>
          </button>
        </PopoverTrigger>
        {stats && stats.total > 0 && (
          <button
            type="button"
            onClick={onToggleFilter}
            title={filterActive ? 'Rimuovi filtro' : 'Mostra solo risorse parziali o mancanti'}
            className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-normal ${filterActive ? 'bg-amber-200 text-amber-900 dark:bg-amber-700/40 dark:text-amber-100' : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'}`}
          >
            <span className="text-green-600 dark:text-green-400">{stats.ok} idonee</span>
            <span className="text-gray-300">·</span>
            <span className="text-amber-600 dark:text-amber-400">{stats.partial} parziali</span>
            <span className="text-gray-300">·</span>
            <span className="text-red-600 dark:text-red-400">{stats.missing} mancanti</span>
          </button>
        )}
      </div>
      <PopoverContent className="w-80" align="start">
        <div className="mb-2 text-sm font-semibold">{svc.product_name}</div>
        <div className="mb-3 text-xs text-gray-500">Modifica le competenze richieste e il livello minimo.</div>
        <div className="space-y-2">
          {draft.length === 0 && (
            <div className="rounded border border-dashed border-gray-300 p-3 text-center text-xs text-gray-500">
              Nessun requisito impostato
            </div>
          )}
          {draft.map(req => {
            const skill = skillsById.get(req.skill_id);
            return (
              <div key={req.skill_id} className="flex items-center gap-2 text-xs">
                <div className="min-w-0 flex-1 truncate font-medium" style={{ color: skill?.color || undefined }}>
                  {skill?.name || '—'}
                </div>
                <Select value={req.min_level} onValueChange={(v) => setLevel(req.skill_id, v as SkillLevel)}>
                  <SelectTrigger className="h-7 w-28 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LEVELS.map(l => (
                      <SelectItem key={l} value={l}>{LEVEL_LABEL[l]} ({LEVEL_RANK[l]})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-red-500 hover:text-red-600"
                  onClick={() => removeSkill(req.skill_id)}
                  title="Rimuovi"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            );
          })}
        </div>

        {available.length > 0 && (
          <div className="mt-3 flex items-center gap-2">
            <Plus className="h-3.5 w-3.5 text-gray-400" />
            <Select value="" onValueChange={(v) => addSkill(v)}>
              <SelectTrigger className="h-7 flex-1 text-xs">
                <SelectValue placeholder="Aggiungi skill…" />
              </SelectTrigger>
              <SelectContent>
                {available.map(s => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={() => setOpen(false)} disabled={saving}>Annulla</Button>
          <Button size="sm" onClick={handleSave} disabled={!canEditServices || saving}>
            {saving && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
            Salva
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

interface AppointmentTypeRow { id: string; name: string; color?: string | null }
interface ProductSearchRow { id: string; name: string; sku?: string | null; product_nature?: string | null }
interface ProductResponse { id: string; name: string; service_type_id?: string | null; product_nature?: string | null }
interface CreateProductPayload {
  name: string;
  product_nature: 'service';
  service_type_id: string;
  sku?: string;
  sell_price?: number;
}
interface UpdateProductPayload {
  service_type_id: string;
  product_nature: 'service';
}

interface NewServiceModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  existingProductIds: Set<string>;
  allSkills: SkillRow[];
  onCreated: () => void;
}

function NewServiceModal({ open, onOpenChange, existingProductIds, allSkills, onCreated }: NewServiceModalProps) {
  const { hasPermission } = usePermissions();
  const canEditServices = hasPermission('pos.catalogo');
  const { toast } = useToast();
  const [mode, setMode] = useState<'existing' | 'new'>('new');
  const [serviceTypes, setServiceTypes] = useState<AppointmentTypeRow[]>([]);
  const [serviceTypeId, setAppointmentTypeId] = useState<string>('');
  const [loadingTypes, setLoadingTypes] = useState(false);
  const [saving, setSaving] = useState(false);

  // Existing-product mode state
  const [searchQ, setSearchQ] = useState('');
  const [searchResults, setSearchResults] = useState<ProductSearchRow[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState<string>('');

  // New-product mode state
  const [newName, setNewName] = useState('');
  const [newSku, setNewSku] = useState('');
  const [newPrice, setNewPrice] = useState<string>('');

  // Optional skill requirements
  const [requirements, setRequirements] = useState<{ skill_id: string; min_level: SkillLevel }[]>([]);

  useEffect(() => {
    if (!open) return;
    setMode('new');
    setAppointmentTypeId('');
    setSearchQ('');
    setSearchResults([]);
    setSelectedProductId('');
    setNewName('');
    setNewSku('');
    setNewPrice('');
    setRequirements([]);
    (async () => {
      setLoadingTypes(true);
      try {
        const r = await apiClient.get<AppointmentTypeRow[]>('/calendar/types?active=true');
        if (r.success) setServiceTypes((r.data || []).filter(t => !!t && !!t.id));
      } catch (e) {
        toast({ title: 'Errore', description: getErrorMessage(e) || 'Impossibile caricare i tipi appuntamento', variant: 'destructive' });
      } finally {
        setLoadingTypes(false);
      }
    })();
  }, [open, toast]);

  useEffect(() => {
    if (mode !== 'existing') return;
    const q = searchQ.trim();
    if (q.length < 3) { setSearchResults([]); setSearching(false); return; }
    let cancelled = false;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const r = await apiClient.get<ProductSearchRow[]>(`/pos/products/search?q=${encodeURIComponent(q)}&limit=20&nature=service`);
        if (cancelled) return;
        if (r.success) {
          const list = (r.data || []).filter(p => !existingProductIds.has(p.id));
          setSearchResults(list);
        }
      } catch (_) {
        // silent
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); setSearching(false); };
  }, [searchQ, mode, existingProductIds]);

  const canSubmit = !!serviceTypeId && (
    (mode === 'existing' && !!selectedProductId) ||
    (mode === 'new' && newName.trim().length > 0)
  );

  const handleSubmit = async () => {
    if (!canEditServices) return;
    if (!canSubmit || saving) return;
    setSaving(true);
    try {
      let productId = selectedProductId;
      let productName = '';

      if (mode === 'new') {
        const payload: CreateProductPayload = {
          name: newName.trim(),
          product_nature: 'service',
          service_type_id: serviceTypeId,
        };
        if (newSku.trim()) payload.sku = newSku.trim();
        const priceNum = parseFloat(newPrice.replace(',', '.'));
        if (!Number.isNaN(priceNum) && priceNum >= 0) payload.sell_price = priceNum;
        const r = await apiClient.post<ProductResponse>('/pos/products', payload);
        if (!r.success || !r.data?.id) throw new Error(r.error || 'Creazione articolo fallita');
        productId = r.data.id;
        productName = r.data.name || newName.trim();
      } else {
        const updatePayload: UpdateProductPayload = {
          service_type_id: serviceTypeId,
          product_nature: 'service',
        };
        const r = await apiClient.put<ProductResponse>(`/pos/products/${productId}`, updatePayload);
        if (!r.success) throw new Error(r.error || 'Aggiornamento articolo fallito');
        productName = r.data?.name || searchResults.find(p => p.id === productId)?.name || '';
      }

      if (requirements.length > 0 && productId) {
        try {
          const reqPayload = requirements.map(r => ({ skill_id: r.skill_id, min_skill_level: r.min_level }));
          const rr = await apiClient.put(`/pos/products/${productId}/skills`, { requirements: reqPayload });
          if (!rr.success) throw new Error(rr.error || 'Salvataggio requisiti fallito');
        } catch (e) {
          toast({ title: 'Servizio creato, requisiti non salvati', description: getErrorMessage(e) || 'Impossibile salvare i requisiti di competenza', variant: 'destructive' });
          onCreated();
          return;
        }
      }

      toast({ title: 'Servizio creato', description: productName ? `"${productName}" è ora collegato al tipo appuntamento` : 'Servizio collegato al tipo appuntamento' });
      onCreated();
    } catch (e) {
      toast({ title: 'Errore', description: getErrorMessage(e) || 'Operazione fallita', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={canEditServices && open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Nuovo servizio</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex rounded-md border border-gray-200 p-1 dark:border-gray-700">
            <button
              type="button"
              onClick={() => setMode('new')}
              className={`flex-1 rounded px-3 py-1.5 text-sm ${mode === 'new' ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/30' : ''}`}
            >Crea nuovo</button>
            <button
              type="button"
              onClick={() => setMode('existing')}
              className={`flex-1 rounded px-3 py-1.5 text-sm ${mode === 'existing' ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/30' : ''}`}
            >Collega esistente</button>
          </div>

          {mode === 'new' ? (
            <div className="space-y-3">
              <div>
                <Label htmlFor="ns-name">Nome servizio *</Label>
                <Input id="ns-name" value={newName} onChange={e => setNewName(e.target.value)} placeholder="Es. Consulenza tecnica" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="ns-sku">SKU (opzionale)</Label>
                  <Input id="ns-sku" value={newSku} onChange={e => setNewSku(e.target.value)} placeholder="SVC-001" />
                </div>
                <div>
                  <Label htmlFor="ns-price">Prezzo (opzionale)</Label>
                  <Input id="ns-price" type="number" min="0" step="0.01" value={newPrice} onChange={e => setNewPrice(e.target.value)} placeholder="0.00" />
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <Label htmlFor="ns-search">Cerca articolo POS esistente</Label>
              <div className="relative">
                <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
                <Input id="ns-search" className="pl-7" value={searchQ} onChange={e => { setSearchQ(e.target.value); setSelectedProductId(''); }} placeholder="Almeno 3 caratteri…" />
              </div>
              <div className="max-h-48 overflow-auto rounded-md border border-gray-200 dark:border-gray-700">
                {searching && (
                  <div className="flex items-center justify-center p-3 text-xs text-gray-500"><Loader2 className="mr-1 h-3 w-3 animate-spin" /> Ricerca…</div>
                )}
                {!searching && searchQ.trim().length >= 3 && searchResults.length === 0 && (
                  <div className="p-3 text-center text-xs text-gray-500">Nessun articolo disponibile (già collegati o non trovati).</div>
                )}
                {!searching && searchQ.trim().length < 3 && (
                  <div className="p-3 text-center text-xs text-gray-400">Digita almeno 3 caratteri per cercare.</div>
                )}
                {searchResults.map(p => (
                  <button
                    type="button"
                    key={p.id}
                    onClick={() => setSelectedProductId(p.id)}
                    className={`flex w-full items-center justify-between border-b border-gray-100 px-3 py-2 text-left text-sm last:border-0 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800 ${selectedProductId === p.id ? 'bg-blue-50 dark:bg-blue-900/30' : ''}`}
                  >
                    <span className="truncate">{p.name}</span>
                    {p.sku && <span className="ml-2 shrink-0 text-xs text-gray-500">{p.sku}</span>}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div>
            <Label htmlFor="ns-type">Tipo appuntamento *</Label>
            <Select value={serviceTypeId} onValueChange={setAppointmentTypeId}>
              <SelectTrigger id="ns-type">
                <SelectValue placeholder={loadingTypes ? 'Caricamento…' : 'Seleziona un tipo appuntamento'} />
              </SelectTrigger>
              <SelectContent>
                {serviceTypes.map(t => (
                  <SelectItem key={t.id} value={t.id}>
                    <span className="inline-flex items-center gap-2">
                      {t.color && <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: t.color }} />}
                      {t.name}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!loadingTypes && serviceTypes.length === 0 && (
              <p className="mt-1 text-xs text-amber-600">Nessun tipo appuntamento configurato. Creane uno dalle impostazioni del calendario.</p>
            )}
          </div>

          <div className="rounded-md border border-gray-200 p-3 dark:border-gray-700">
            <div className="mb-1 flex items-center justify-between">
              <Label className="text-sm">Competenze richieste (opzionale)</Label>
              <span className="text-[11px] text-gray-500">{requirements.length} skill</span>
            </div>
            <p className="mb-2 text-xs text-gray-500">Aggiungi le skill richieste e il livello minimo. Potrai modificarle anche in seguito dalla colonna del servizio.</p>
            {allSkills.length === 0 ? (
              <p className="text-xs text-amber-600">Nessuna competenza definita. Crea prima le skill in HR.</p>
            ) : (
              <div className="space-y-2">
                {requirements.length === 0 && (
                  <div className="rounded border border-dashed border-gray-300 p-2 text-center text-xs text-gray-500 dark:border-gray-600">
                    Nessun requisito impostato
                  </div>
                )}
                {requirements.map(req => {
                  const skill = allSkills.find(s => s.id === req.skill_id);
                  return (
                    <div key={req.skill_id} className="flex items-center gap-2 text-xs">
                      <div className="min-w-0 flex-1 truncate font-medium" style={{ color: skill?.color || undefined }}>
                        {skill?.name || '—'}
                      </div>
                      <Select
                        value={req.min_level}
                        onValueChange={(v) => setRequirements(prev => prev.map(r => r.skill_id === req.skill_id ? { ...r, min_level: v as SkillLevel } : r))}
                      >
                        <SelectTrigger className="h-7 w-32 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {LEVELS.map(l => (
                            <SelectItem key={l} value={l}>{LEVEL_LABEL[l]} ({LEVEL_RANK[l]})</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-red-500 hover:text-red-600"
                        onClick={() => setRequirements(prev => prev.filter(r => r.skill_id !== req.skill_id))}
                        title="Rimuovi"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  );
                })}
                {(() => {
                  const usedIds = new Set(requirements.map(r => r.skill_id));
                  const available = allSkills.filter(s => !usedIds.has(s.id));
                  if (available.length === 0) return null;
                  return (
                    <div className="flex items-center gap-2">
                      <Plus className="h-3.5 w-3.5 text-gray-400" />
                      <Select
                        value=""
                        onValueChange={(v) => {
                          if (!v) return;
                          setRequirements(prev => prev.some(r => r.skill_id === v) ? prev : [...prev, { skill_id: v, min_level: 'base' }]);
                        }}
                      >
                        <SelectTrigger className="h-7 flex-1 text-xs">
                          <SelectValue placeholder="Aggiungi skill…" />
                        </SelectTrigger>
                        <SelectContent>
                          {available.map(s => (
                            <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>Annulla</Button>
          <Button onClick={handleSubmit} disabled={!canEditServices || !canSubmit || saving}>
            {saving && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
            {mode === 'new' ? 'Crea servizio' : 'Collega servizio'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
